import { RefObject, UIEvent, useCallback, useEffect } from 'react';
import { UseInfiniteQueryResult } from 'react-query';
import { GetCompaniesResponse } from 'api/services/company/company-service.schema';

const PIXELS_TO_BOTTOM = 300;

export const useCompaniesInfiniteScroll = (
  scrollContainerRef: RefObject<HTMLDivElement | null>,
  companiesQuery: UseInfiniteQueryResult<GetCompaniesResponse>,
  flatDataLength: number
) => {
  const tryFetchMore = useCallback(
    (scrollContainer?: HTMLDivElement | null) => {
      const container = scrollContainer ?? scrollContainerRef.current;
      const pages = companiesQuery.data?.pages;

      if (!container || !pages?.length) {
        return;
      }

      const lastPage = pages[pages.length - 1];

      if (!lastPage.hasNextPage || companiesQuery.isFetching) {
        return;
      }

      const { scrollHeight, scrollTop, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceFromBottom < PIXELS_TO_BOTTOM) {
        void companiesQuery.fetchNextPage();
      }
    },
    [
      companiesQuery.data?.pages,
      companiesQuery.fetchNextPage,
      companiesQuery.isFetching,
      scrollContainerRef,
    ]
  );

  useEffect(() => {
    tryFetchMore();
  }, [tryFetchMore, flatDataLength]);

  const onContentScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      tryFetchMore(event.currentTarget);
    },
    [tryFetchMore]
  );

  return { onContentScroll };
};
